import APIClient from './index';
import _ from 'lodash';

const get = (path, params) => {
  const qs = params ? `?${new URLSearchParams(_.omitBy(params, v => v == null || v === '')).toString()}` : '';
  return new Promise((resolve, reject) => {
    APIClient.get(`AdminSpotlights/${path}${qs}`)
      .then(response => resolve(response.data))
      .catch(err => reject(_.get(err, 'response.data.error') || _.get(err, 'data.error') || 'Request failed'));
  });
};

const post = (path, body) => {
  return new Promise((resolve, reject) => {
    APIClient.post(`AdminSpotlights/${path}`, body)
      .then(response => resolve(response.data))
      .catch(err => reject(_.get(err, 'response.data.error') || _.get(err, 'data.error') || 'Request failed'));
  });
};

const patch = (path, body) => {
  return new Promise((resolve, reject) => {
    APIClient.patch(`AdminSpotlights/${path}`, body)
      .then(response => resolve(response.data))
      .catch(err => reject(_.get(err, 'response.data.error') || _.get(err, 'data.error') || 'Request failed'));
  });
};

// Overview
export const getOverview = params => get('overview', params);
export const getRankings = params => get('rankings', params);

// Model eval (recommendation shadow-eval digest)
export const getModelEval = params => get('model-eval', params);

// Brands
export const getBrands = params => get('brands', params);
export const getBrandDetail = brandId => get(`brands/${brandId}/detail`);
export const searchBrands = q => get('brands/search', { q });
export const setSpotlightEnabled = (brandId, value) => post(`brands/${brandId}/spotlight-enabled`, { value });
export const setAllowNonDtcDestinationLinks = (brandId, value) => post(`brands/${brandId}/allow-non-dtc-links`, { value });
export const setAllowBelowMinDailyBudget = (brandId, value) => post(`brands/${brandId}/allow-below-min-budget`, { value });
export const unpauseBillingPausedSpotlights = brandId => post(`brands/${brandId}/unpause-billing`);
// Strategies rollout flags on BrandSettings.featureFlags. flags maps a key to true, false, or null (clears it).
export const getBrandFeatureFlags = brandId => get(`brands/${brandId}/feature-flags`);
export const setBrandFeatureFlags = (brandId, flags) => post(`brands/${brandId}/feature-flags`, { flags });
export const setBrandFeatureFlagsBulk = (brandIds, flags) => post('brands/feature-flags-bulk', { brandIds, flags });

// Creators
export const getCreators = params => get('creators', params);
export const getCreatorDetail = userId => get(`creators/${userId}/detail`);
export const setCreatorVip = (userId, value) => post(`creators/${userId}/vip`, { value });
// Creator-level Spotlights kill switch. disabled=true disables (with optional reason); false re-enables.
export const setCreatorSpotlightsDisabled = (userId, disabled, reason) => post(`creators/${userId}/spotlights-disabled`, { disabled, reason });
export const searchCreators = q => get('creators/search', { q });
export const setCreatorVipBulk = (userIds, value = true) => post('creators/vip-bulk', { userIds, value });
// Ad-platform grant ops live on the AdPlatformGrants routes (admin-scoped), not AdminSpotlights.
// Recheck forces grant detection past the cache; unlink releases the platform asset (TikTok: the
// TT_ACCOUNT) to reclaim its Business Center slot, pausing the creator's live ads first.
export const recheckAdPlatformGrant = (platform, userId) =>
  new Promise((resolve, reject) => {
    APIClient.post(`AdPlatformGrants/${platform}/${userId}/recheck`)
      .then(response => resolve(response.data))
      .catch(err => reject(_.get(err, 'response.data.error') || _.get(err, 'data.error') || 'Request failed'));
  });
export const unlinkAdPlatformGrant = (platform, userId) =>
  new Promise((resolve, reject) => {
    APIClient.post(`AdPlatformGrants/${platform}/${userId}/recheck`, { unlink: true })
      .then(response => resolve(response.data))
      .catch(err => reject(_.get(err, 'response.data.error') || _.get(err, 'data.error') || 'Request failed'));
  });

// Billing
export const getBilling = params => get('billing', params);
export const getBrandInvoices = brandId => get(`billing/${brandId}/invoices`);
export const getBrandPaymentMethods = brandId => get(`billing/${brandId}/payment-methods`);
export const setBrandPaymentMethod = (brandId, paymentMethodId) => post(`billing/${brandId}/payment-method`, { paymentMethodId });
// Net terms for Spotlights ad-spend invoices: number of days until due (0 = off, auto-charge as normal).
export const setSpotlightNetTerms = (brandId, days) => post(`billing/${brandId}/net-terms`, { days });
// Allow completing Spotlights onboarding without a payment method; invoices are sent for manual payment.
export const setSpotlightSkipPaymentMethod = (brandId, value) => post(`billing/${brandId}/skip-payment-method`, { value });
// Supplemental invoice for a closed period, free-form line items: preview returns collection eligibility + what's already invoiced.
export const getSupplementalInvoicePreview = (brandId, params) => get(`billing/${brandId}/supplemental-preview`, params);
export const createSupplementalInvoice = (brandId, body) => post(`billing/${brandId}/supplemental-invoice`, body);
export const getScheduledInvoicePreview = brandId => get(`billing/${brandId}/scheduled-invoice-preview`);
export const createScheduledInvoice = (brandId, body) => post(`billing/${brandId}/scheduled-invoice`, body);
// Credits deducted from upcoming scheduled ad-spend invoices (never below $0; remainders roll forward).
export const getBillingCredits = brandId => get(`billing/${brandId}/credits`);
export const addBillingCredit = (brandId, body) => post(`billing/${brandId}/credits`, body);
export const cancelBillingCredit = (brandId, creditId) => post(`billing/${brandId}/credits/${creditId}/cancel`);
// Scheduled ad-spend invoice grouping: 'combined' (one invoice) or 'per_group' (one invoice per set).
export const setInvoiceGrouping = (brandId, value) => post(`billing/${brandId}/invoice-grouping`, { value });
export const setInvoiceExemption = (brandId, body) => post(`billing/${brandId}/invoice-exemption`, body);

// Trials
export const getTrials = () => get('trials');
export const grantTrial = body => post('trials/grant', body);
// Grant evergreen Spotlights at $0 (included in package, not separately billable).
export const grantEvergreen = body => post('trials/grant-evergreen', body);
export const cancelTrial = brandId => post('trials/cancel', { brandId });
export const editTrial = (brandId, days) => patch('trials/edit', { brandId, days });

// Ad automation
export const getAutomation = params => get('automation', params);

// Ad automation routines (editable, versioned config the extension consumes)
export const saveRoutine = body => post('automation/routines', body);
export const getRoutineVersions = (platform, routineKey) => get('automation/routines/versions', { platform, routineKey });
export const activateRoutineVersion = id => post(`automation/routines/${id}/activate`);

// Triage ("Error section"): the unified queue of open issues (auto-detected events + admin marks).
export const getTriageQueue = () => get('triage');
// Open item count for the tab badge.
export const getTriageCount = () => get('triage/count');
// Paginated list of cleared items for the Resolved tab.
export const getResolvedTriage = params => get('triage/resolved', params);
// Resolve one or many triage items by SpotlightEvent id (single button + bulk bar).
export const bulkResolveTriage = eventIds => post('triage/bulk-resolve', { eventIds });
// Mark a spotlight errored, moving it into the triage queue. errorType is a SPOTLIGHT_ERROR_REASONS key.
export const markSpotlightError = (spotlightId, errorType, reason) => post(`spotlights/${spotlightId}/mark-errored`, { errorType, reason });

// Daily digest. report: 'performance' (Report A) | 'needs_attention' (Report B); edition: 'morning' | 'evening'.
export const getDailySummary = ({ report = 'performance', date, edition } = {}) =>
  get('daily-summary', { report, ...(date ? { date } : {}), ...(edition ? { edition } : {}) });
export const getDailySummaryDates = () => get('daily-summary/dates');
